import { useState, useRef, useEffect } from 'react';
import { Button } from '../primitives/';
import { LuTrash2 } from 'react-icons/lu';
import { useOutletContext } from 'react-router-dom';
import useTimedError from '../../hooks/useTimedError';
import { useDatabase } from '../../contexts/DatabaseContext';
import { useTranslation } from '../../hooks/useTranslation';
import { LIMITS } from '../../Limits';

export default function AutoReplySettings({ config, workspaceId, workspaceType, setConfig, getRandomAutoReply, autoReplies, setAutoReplies, hasChangesAutoReply, requestedVisualTrigger, requestedVisualIndex }) {
    
    const [err, setErr] = useTimedError();
    const [errors, setErrors] = useState(
        (config?.autoReplies || []).map(() => ({
            option: null,
            reply: null
        }))
    );
    const [buttonText, setButtonText] = useState('Update Settings');
    const [loading, setLoading] = useState(false);
    const { updateWorkspaceConfigDoc, createAutoReplyActionEvent } = useDatabase();
    const t = useTranslation();

    const addAutoReply = () => {
        if (autoReplies.length < 4) {
            setAutoReplies([...autoReplies, getRandomAutoReply()]);
            setErrors([...errors, { option: null, reply: null }]);
        } else {
            setErr(t('You can only have up to 4 auto replies.'));
        }
    };

    function validateTextInput(field, value) {
        if (field === 'option') {
            return value.length <= LIMITS.OPTION && value.length > 0;
        } else if (field === 'reply') {
            return value.length <= LIMITS.REPLY && value.length > 0;
        }
        return true;
    }
  
    const updateAutoReply = (index, field, value) => {
        requestedVisualTrigger.current = 'autoReply';
        requestedVisualIndex.current = index;
        setAutoReplies(prev => {
            const newAutoReplies = [...prev];
            newAutoReplies[index] = { ...newAutoReplies[index], [field]: value };
            return newAutoReplies;
        });
        
        const newErrors = [...errors];
        if (validateTextInput(field, value)) {
            newErrors[index] = { ...newErrors[index], [field]: null };
        } else {
            newErrors[index] = { ...newErrors[index], [field]: t(LIMITS[`${field}Error`]) };
        }
        setErrors(newErrors);
    };
  
    const removeAutoReply = (index) => {
        setAutoReplies(autoReplies.filter((_, i) => i !== index));
        setErrors(errors.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            createAutoReplyActionEvent(workspaceType, workspaceId, autoReplies.length);
            await updateWorkspaceConfigDoc(workspaceId, { autoReplies });
            setConfig(prev => ({ ...prev, autoReplies }));
        } catch (error) {
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    message: "An error occurred while updating the workspace config.",
                    action: "auto_reply",
                }
            }));
        } finally {
            setLoading(false);
            setButtonText('Published!');
            setTimeout(() => {
                setButtonText('Update Settings');
            }, 2000);
        }
    }

    function hasErrors() {
        return errors.some(error => error.option || error.reply);
    }

    return (
        <div className="settings-container-outer">
            {autoReplies.map((autoReply, idx) => (
                <AutoReplyCard 
                    key={idx} 
                    autoReply={autoReply} 
                    idx={idx} 
                    updateAutoReply={updateAutoReply} 
                    removeAutoReply={removeAutoReply}
                    error={errors[idx]} 
                />
            ))}
            
            <Button onClick={addAutoReply} className="settings" error={err}>
                {err || 'Add Auto Reply'}
            </Button>
            <Button className="submit settings"
                onClick={handleSubmit}
                loading={loading}
                loadingFinish={buttonText === 'Published!'}
                disabled={!hasChangesAutoReply || hasErrors()}
            >
                {buttonText}
            </Button>
        </div>
    );
}

function AutoReplyCard({ autoReply, idx, updateAutoReply, removeAutoReply, error }) {
    const { mobile } = useOutletContext();
    const [showDelete, setShowDelete] = useState(!mobile);
    const cardRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);

    const handleTouchStart = (e) => {
        if (mobile) {
            setStartX(e.touches ? e.touches[0].clientX : e.clientX);
            setIsDragging(false);
        }
    };

    const handleMouseDown = (e) => {
        if (mobile) {
            setStartX(e.clientX);
            setIsDragging(false);
        }
    };

    const handleTouchMove = (e) => {
        if (mobile) {
            const currentX = e.touches ? e.touches[0].clientX : e.clientX;
            const diff = startX - currentX;
            
            // If dragged more than 20px, show delete button
            if (diff > 20 && !isDragging) {
                setIsDragging(true);
                setShowDelete(true);
            }
            
            if (diff < -20 && !isDragging) {
                setIsDragging(false);
                setShowDelete(false);
            }
        }
    };

    const handleMouseMove = (e) => {
        if (mobile && startX !== 0) {
            const currentX = e.clientX;
            const diff = startX - currentX;
            
            // If dragged more than 20px, show delete button
            if (diff > 20 && !isDragging) {
                setIsDragging(true);
                setShowDelete(true);
            }
            
            if (diff < -20 && !isDragging) {
                setIsDragging(false);
                setShowDelete(false);
            }
        }
    };

    const handleMouseUp = () => {
        if (mobile) {
            setStartX(0);
        }
    };

    useEffect(() => {
        // Add global mouse up handler
        document.addEventListener('mouseup', handleMouseUp);
        
        // Cleanup
        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    return (
        <div className="input-settings-group-inner-row">
            <div 
                className={`auto-card ${showDelete && mobile ? 'auto-card-with-delete' : ''}`}
                ref={cardRef}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
            >
                <input
                    type="text"
                    className="auto-card-input"
                    placeholder={'Enter prompt'}
                    value={autoReply.option}
                    onChange={(e) => updateAutoReply(idx, 'option', e.target.value)}
                />
                {error?.option && <p className="auto-card-input-error">{error.option}</p>}
                <textarea
                    type="text"
                    className="auto-card-input"
                    placeholder={'Enter reply message'}
                    value={autoReply.reply}
                    onChange={(e) => updateAutoReply(idx, 'reply', e.target.value)}
                />
                {error?.reply && <p className="auto-card-input-error bottom">{error.reply}</p>}
            </div>
            {(showDelete || !mobile) && (
                <button 
                    className="auto-card-delete-button"
                    onClick={() => removeAutoReply(idx)}
                >
                    <LuTrash2 />
                </button>
            )}
        </div>
    );
}